/**
 * CustomerSummaryInfoCard — replaces the previous `AiSummaryPanel` in the
 * normal admin workflow.
 *
 * The customer-facing summary is now generated deterministically from the
 * Pristine Water Test Summary & Action Plan template. AI / Claude
 * narratives are no longer used for customer-facing output and are no
 * longer required to approve or send a report.
 *
 * This card surfaces:
 *   • An explanatory note for admins
 *   • A "Download Pristine Summary" button (standalone PDF)
 *   • A collapsed "Internal AI summary (legacy)" disclosure that mounts
 *     the original AiSummaryPanel ONLY when admins explicitly expand it.
 *     The legacy panel remains available for back-reference but is
 *     deliberately out of the normal workflow path.
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Download, Info } from "lucide-react";
import { toast } from "sonner";
import { AiSummaryPanel } from "./AiSummaryPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function downloadPristineSummary(testId) {
  const token = localStorage.getItem("scwt_token");
  axios
    .get(`${API}/admin/reports/${testId}/pristine-summary`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    })
    .then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pristine-summary-${testId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })
    .catch((err) => {
      const msg = err?.response?.data?.detail || "Could not download Pristine Summary";
      toast.error(msg);
    });
}

export function CustomerSummaryInfoCard({ testId, isManager }) {
  const [showLegacy, setShowLegacy] = useState(false);

  return (
    <section
      data-testid="customer-summary-info-card"
      className="rounded-3xl bg-white border border-slate-200 p-6 space-y-4"
    >
      <div className="flex items-start gap-3">
        <Info className="w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 text-sm">Customer Summary</h3>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            The customer-facing summary is generated using the approved Pristine
            Water Systems template and deterministic recommendation logic. No
            AI-generated summary approval is required.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="customer-summary-download-pristine"
          variant="outline"
          size="sm"
          onClick={() => downloadPristineSummary(testId)}
          className="rounded-full font-semibold"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" />
          Download Pristine Summary
        </Button>
      </div>

      <details
        className="border-t border-slate-100 pt-3 mt-2"
        data-testid="customer-summary-legacy-disclosure"
        onToggle={(e) => setShowLegacy(e.currentTarget.open)}
      >
        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 select-none">
          Internal AI summary (legacy reference only — not used for customer output)
        </summary>
        {showLegacy && (
          <div className="mt-3">
            <AiSummaryPanel
              testId={testId}
              isPublished={false}
              isManager={isManager}
            />
          </div>
        )}
      </details>
    </section>
  );
}

export default CustomerSummaryInfoCard;
